/**
 * Embedded images inside an `.xlsx` attachment — the photographs a customer drops into a
 * requisition workbook (a nameplate, a part, a damaged fitting) alongside the item list.
 * `xlsxPreview.ts` renders the cell grid; this module is the other half of the file the grid
 * alone can't show, because those photos live outside any cell — they're OOXML "drawing"
 * objects anchored on top of a sheet, not spreadsheet content `read-excel-file` ever sees.
 *
 * Verified against REAL requisition workbooks, not assumed: two of the mail screen's live
 * attachments ("REQ N 1 Electrical Requsition for store.xlsx" and the "DTA-10 DECK REQUISITION"
 * series) carry 4-7 embedded JPEGs each, every one of them on a dedicated `photos-docs` /
 * `PHOTOS-DOCS` sheet that otherwise has ZERO cell data — see `xlsxPreview.ts`'s
 * `fetchAndParseXlsxPreview`, which keeps a sheet like that in the preview specifically so this
 * isn't silently dropped by the "sheet has no data" filter that exists for genuinely blank
 * sheets. `xlsxImages.test.ts` fixtures that exact shape (a `<sheetData/>` with no `<c>` cells,
 * one `<drawing>` reference) rather than a toy.
 *
 * ---- What "sheet association" costs, and what it doesn't buy ----
 *
 * An `.xlsx` is a zip; images sit at `xl/media/*`. Which SHEET an image belongs to is a chain of
 * four small relationship files: `xl/workbook.xml` (sheet name → a workbook-level `r:id`),
 * `xl/_rels/workbook.xml.rels` (that `r:id` → the sheet's own part path), the sheet's own
 * `_rels/*.rels` (a `<drawing>` `r:id` → a `drawingN.xml` path), and that drawing's `_rels/*.rels`
 * (each `<a:blip r:embed>` `r:id` → a `xl/media/*` path). Every one of those is a short,
 * machine-generated, single-namespace attribute list — never hand-authored, so it's reliably
 * well-formed — which is why walking it with small regexes (`findTags`/`attr` below) is a modest
 * addition rather than a real XML-parser integration; `saxen` (available the same way `fflate` is
 * — see this repo's `package.json`) would buy nothing extra for flat attribute reads like these.
 *
 * What this DOESN'T do is resolve the anchor element's own cell reference (`<xdr:from><xdr:col>`
 * etc.) to place an image at a precise row/column. That is a materially bigger lift — two anchor
 * shapes (`oneCellAnchor`/`twoCellAnchor`), EMU-to-pixel/column math, and a rendering surface
 * that overlays images on specific grid cells rather than listing them — and the real workbooks
 * above don't reward it: their images sit on a sheet with no cell grid at all. Sheet-level
 * grouping is the right stopping point; see the header of `xlsxPreview.ts` for how it's surfaced.
 *
 * ---- Extraction never hides an image, even when it can't show one ----
 *
 * Every image `xl/media/*` actually contains is returned — `images.length` in the `'ok'` case is
 * always the true count, independent of whether association or format-support succeeded for any
 * of them. A `.png`/`.jpeg`/`.gif`/`.bmp`/`.webp` gets a `mime` the browser can render; an
 * `.emf`/`.wmf` (common from an Office "paste special") or anything else gets `mime: null` — the
 * caller shows it as a named, downloadable, clearly-labelled tile instead of a broken `<img>`.
 * Only a failure to even list `xl/media/*` (a corrupt zip, a shape this parser's four-file walk
 * genuinely can't read) reports `'scan_failed'` — realistically near-unreachable in practice,
 * since `fetchAndParseXlsxPreview` only calls this after `read-excel-file` has already unzipped
 * the same bytes successfully, but handled honestly rather than assumed away.
 */

/** One image found at `xl/media/*`, decoded as far as this module goes (bytes + format), plus
 * whatever sheet association it could resolve. */
export interface XlsxImage {
  /** Full zip path, e.g. `"xl/media/image1.jpeg"` — stable, used as the React key. */
  path: string
  /** File name only, e.g. `"image1.jpeg"`. */
  name: string
  /** Lowercased extension without the dot, e.g. `"jpeg"`, `"emf"`. Empty string if the media
   * file name has none (seen in the wild for OOXML `.png`-less drawing artifacts). */
  format: string
  /** A browser-renderable MIME type, or `null` when `format` isn't one this module knows how to
   * put in an `<img>` — the caller must show those as "can't preview this format", never guess. */
  mime: string | null
  /** Raw file bytes — enough on its own to build an object URL (render or download) without a
   * second fetch. Always backed by a plain `ArrayBuffer` (never `ArrayBufferLike`/
   * `SharedArrayBuffer`) — see where this is built in `extractXlsxImages` — so it's directly
   * usable as a `BlobPart` with no cast at the call site. */
  bytes: Uint8Array<ArrayBuffer>
  /** The worksheet this image's drawing was resolved to, or `null` when the relationship chain
   * above didn't resolve (an unrecognised workbook shape) — an honest "not known", never a
   * guess at which sheet it's probably on. */
  sheetName: string | null
}

export type XlsxImageExtraction =
  | { status: 'none' }
  | { status: 'ok'; images: XlsxImage[] }
  | { status: 'scan_failed'; detail: string }

/** Formats this module can hand straight to an `<img>`/`<a download>` as-is. `emf`/`wmf` (GDI
 * vector formats Windows Office tools default to for "Insert > Picture" in older builds) and
 * anything else fall through to `mime: null` — no browser renders those natively. */
const RENDERABLE_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  bmp: 'image/bmp',
  webp: 'image/webp',
}

/** Matches every `<[ns:]localName ...>` opening (or self-closing) tag in `xml`, ignoring any
 * namespace prefix — this repo's fixtures and the real workbooks above both use `xdr:`/`a:`/`r:`
 * prefixes for these elements, but the prefix itself isn't meaningful here (there's exactly one
 * plausible element with each local name in these part types). Exported: `xlsxLayout.ts` (merge
 * cells, column widths) walks the same kind of machine-generated, single-namespace XML and reuses
 * this rather than re-implementing it. */
export function findTags(xml: string, localName: string): string[] {
  const re = new RegExp(`<(?:[\\w.-]+:)?${localName}\\b[^>]*?/?>`, 'g')
  return xml.match(re) ?? []
}

/** Reads one `name="value"` attribute out of an already-matched tag string. Exported alongside
 * `findTags` for the same reason. */
export function attr(tag: string, name: string): string | null {
  const re = new RegExp(`${name}="([^"]*)"`)
  const match = re.exec(tag)
  return match ? match[1] : null
}

/** `Id` → `{ type, target }` for every `<Relationship>` in a `.rels` part. */
function parseRelationships(xml: string): Map<string, { type: string; target: string }> {
  const map = new Map<string, { type: string; target: string }>()
  for (const tag of findTags(xml, 'Relationship')) {
    const id = attr(tag, 'Id')
    const target = attr(tag, 'Target')
    if (id && target) map.set(id, { type: attr(tag, 'Type') ?? '', target })
  }
  return map
}

/** OOXML relationship `Target`s are relative to the directory of the part that OWNS the `.rels`
 * file referencing them (never to the `.rels` file's own `_rels/` directory) — e.g. a target in
 * `xl/worksheets/_rels/sheet1.xml.rels` resolves against `xl/worksheets/`, not
 * `xl/worksheets/_rels/`. `ownerPartPath` is that owning part's zip path. */
function resolveRelTarget(ownerPartPath: string, target: string): string {
  if (target.startsWith('/')) return target.slice(1)
  const slash = ownerPartPath.lastIndexOf('/')
  const baseDir = slash === -1 ? '' : ownerPartPath.slice(0, slash)
  const stack: string[] = []
  for (const part of (baseDir ? `${baseDir}/${target}` : target).split('/')) {
    if (part === '' || part === '.') continue
    if (part === '..') stack.pop()
    else stack.push(part)
  }
  return stack.join('/')
}

/** The `.rels` part that describes `partPath`'s own relationships, e.g.
 * `xl/worksheets/sheet1.xml` → `xl/worksheets/_rels/sheet1.xml.rels`. */
function relsPathFor(partPath: string): string {
  const slash = partPath.lastIndexOf('/')
  const dir = slash === -1 ? '' : partPath.slice(0, slash)
  const file = slash === -1 ? partPath : partPath.slice(slash + 1)
  return `${dir ? `${dir}/` : ''}_rels/${file}.rels`
}

/** `xl/media/image1.jpeg` → `image1.jpeg`. */
function baseName(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1)
}

/** Walks `xl/workbook.xml` → `xl/_rels/workbook.xml.rels` to build `sheet name → the worksheet
 * part's own zip path` (e.g. `"Sheet1" → "xl/worksheets/sheet1.xml"`) — the first hop of the same
 * relationship chain this module's header describes. Exported because `xlsxLayout.ts` (merge
 * cells, column widths — a separate zip walk of the same bytes, same reasoning as this module's
 * own existence) needs exactly this hop and no more: unlike image resolution, merges/widths live
 * directly in the worksheet's own XML, with no `<drawing>`/media indirection to follow further. */
export function resolveWorksheetPaths(entries: Record<string, Uint8Array>): Map<string, string> {
  const decoder = new TextDecoder('utf-8')
  const text = (path: string): string | null => {
    const bytes = entries[path]
    return bytes ? decoder.decode(bytes) : null
  }

  const paths = new Map<string, string>()
  const workbookXml = text('xl/workbook.xml')
  const workbookRelsXml = text('xl/_rels/workbook.xml.rels')
  if (!workbookXml || !workbookRelsXml) return paths
  const workbookRels = parseRelationships(workbookRelsXml)

  for (const sheetTag of findTags(workbookXml, 'sheet')) {
    const name = attr(sheetTag, 'name')
    const rId = attr(sheetTag, 'r:id')
    if (!name || !rId) continue
    const sheetRel = workbookRels.get(rId)
    if (!sheetRel) continue
    paths.set(name, resolveRelTarget('xl/workbook.xml', sheetRel.target))
  }
  return paths
}

/** Walks each worksheet's own `<drawing>` → its `.rels` → the drawing part → ITS `.rels` to build
 * `media zip path → sheet name`, starting from `resolveWorksheetPaths` above. Best effort
 * throughout: any missing or unrecognised part along the chain just leaves that image (or that
 * sheet's images) unresolved rather than throwing — a workbook shape this walk doesn't expect
 * should degrade to "images with no sheet label", never lose the images or crash the whole
 * extraction (see this module's header). */
function resolveImageSheets(entries: Record<string, Uint8Array>): Map<string, string> {
  const decoder = new TextDecoder('utf-8')
  const text = (path: string): string | null => {
    const bytes = entries[path]
    return bytes ? decoder.decode(bytes) : null
  }

  const sheetByMediaPath = new Map<string, string>()
  const worksheetPaths = resolveWorksheetPaths(entries)

  for (const [name, worksheetPath] of worksheetPaths) {
    const worksheetXml = text(worksheetPath)
    const drawingTag = worksheetXml ? findTags(worksheetXml, 'drawing')[0] : undefined
    const drawingRId = drawingTag ? attr(drawingTag, 'r:id') : null
    if (!drawingRId) continue

    const worksheetRelsXml = text(relsPathFor(worksheetPath))
    if (!worksheetRelsXml) continue
    const drawingRel = parseRelationships(worksheetRelsXml).get(drawingRId)
    if (!drawingRel) continue
    const drawingPath = resolveRelTarget(worksheetPath, drawingRel.target)

    const drawingXml = text(drawingPath)
    if (!drawingXml) continue
    const drawingRelsXml = text(relsPathFor(drawingPath))
    if (!drawingRelsXml) continue
    const imageRels = parseRelationships(drawingRelsXml)

    for (const blipTag of findTags(drawingXml, 'blip')) {
      const embedId = attr(blipTag, 'r:embed')
      const imageRel = embedId ? imageRels.get(embedId) : undefined
      if (!imageRel) continue
      const mediaPath = resolveRelTarget(drawingPath, imageRel.target)
      if (!sheetByMediaPath.has(mediaPath)) sheetByMediaPath.set(mediaPath, name)
    }
  }

  return sheetByMediaPath
}

/**
 * Pure — no fetch, no DOM. Unzips `bytes` (`fflate`, dynamically imported so it only enters the
 * bundle when an xlsx preview actually opens, same as `read-excel-file` in `xlsxPreview.ts`),
 * lists every `xl/media/*` entry, and resolves each one's sheet where the relationship chain
 * allows it. Takes raw bytes rather than a `Blob` so it's directly callable from a test fixture
 * built with `fflate.zipSync`.
 */
export async function extractXlsxImages(bytes: Uint8Array): Promise<XlsxImageExtraction> {
  let entries: Record<string, Uint8Array>
  try {
    const { unzipSync } = await import('fflate')
    entries = unzipSync(bytes)
  } catch (error) {
    return {
      status: 'scan_failed',
      detail: error instanceof Error ? error.message : 'Could not read this file as a zip archive.',
    }
  }

  const mediaPaths = Object.keys(entries)
    .filter((path) => path.startsWith('xl/media/'))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  if (mediaPaths.length === 0) return { status: 'none' }

  // Best-effort, never fatal to the image list itself — see `resolveImageSheets`'s header.
  let sheetByMediaPath: Map<string, string>
  try {
    sheetByMediaPath = resolveImageSheets(entries)
  } catch {
    sheetByMediaPath = new Map()
  }

  const images: XlsxImage[] = mediaPaths.map((path) => {
    const name = baseName(path)
    const format = (name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '').toLowerCase()
    return {
      path,
      name,
      format,
      mime: RENDERABLE_MIME[format] ?? null,
      // `fflate`'s `Unzipped` entries are typed `Uint8Array<ArrayBufferLike>` (it accepts a
      // `SharedArrayBuffer`-backed view in principle) — copying into a fresh `Uint8Array` gives
      // one backed by a plain `ArrayBuffer`, which is what `Blob`'s `BlobPart` (used when the UI
      // builds an object URL to render/download this image) actually requires.
      bytes: new Uint8Array(entries[path]),
      sheetName: sheetByMediaPath.get(path) ?? null,
    }
  })

  return { status: 'ok', images }
}
